import { BarChart2, Calendar, ChevronDown, RefreshCw } from "lucide-react"
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Button } from "../../../components/ui/button";
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
  } from "../../../components/ui/table";
import { MouseEventHandler, useState, useEffect, useRef, useCallback } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { useAuth } from "../../../context/AuthContext";
import { dashboardService, DashboardStats, RevenueData } from "../../../utils/dashboardService";
import { CourseDisplayData } from "../course/courseList";
import { getInstructorCourses } from "../../../utils/firebaseInstructorCourses";
import { format } from "date-fns";
import apiService from "../../../utils/apiService";
import { API_BASE_URL } from "../../../lib/api";
import courseWatchTimeService, { CourseWatchTimeData } from "../../../utils/courseWatchTimeService";
import { useNavigate } from "react-router-dom";

// Monthly Revenue Summary interface matching API response
interface MonthlyRevenueSummary {
  month: string;
  year: number;
  totalRevenue: number;
  totalTax: number;
  totalPlatformFee: number;
  totalInstructorShare: number;
  subscriptionRevenue: number;
  courseRevenue: number;
  withoutHoldingTax: number;
  processedDate: Date | null;
  breakdown: any[];
  watchMinutes: number;
  totalWatchTime: number;
}

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const yearsList = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];

export const Overview = () =>{
    const navigate = useNavigate();
    const [revenueMonthly, setRevenueMonthly] = useState<MonthlyRevenueSummary[]>([]);
    const [showmonthWiseReport, setShowMonthWiseReport] = useState(false);
    const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
    const [revenueStats, setRevenueStats] = useState<RevenueData[]>([]);
    const [courses, setCourses] = useState<CourseDisplayData[]>([]);
    const [courseWatchTimeData, setCourseWatchTimeData] = useState<CourseWatchTimeData[]>([]);
    const [watchTimePricingFilter, setWatchTimePricingFilter] = useState<'all' | 'paid' | 'free'>('all');

    // Month and Year filter for Course-wise watch time
    const [watchTimeMonth, setWatchTimeMonth] = useState<string>("all"); // "all" | "1".."12"
    const [watchTimeYear, setWatchTimeYear] = useState<string>("all"); // "all" | "2026" | ...
    const [watchTimeLoading, setWatchTimeLoading] = useState<boolean>(false);
    const isWatchTimeInitialMount = useRef(true);

    const [selectedCourse, setSelectedCourse] = useState<string >('all-courses');
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    // Format watch time function
    const formatWatchTime = (minutes: number): string => {
        if (minutes < 60) {
            return `${Math.round(minutes)} Min`;
        } else if (minutes < 1440) { // Less than 24 hours
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = Math.round(minutes % 60);
            return `${hours}h ${remainingMinutes}m`;
        } else {
            const days = Math.floor(minutes / 1440);
            const remainingHours = Math.floor((minutes % 1440) / 60);
            return `${days}d ${remainingHours}h`;
        }
    };

    const loadWatchTimeData = useCallback(async (monthVal: string, yearVal: string) => {
        const instructorId = user?.UserName || user?.email;
        if (!instructorId) return;

        try {
            setWatchTimeLoading(true);
            const data = await courseWatchTimeService.getCourseWatchTimeData(
                instructorId,
                'all',
                yearVal === 'all' ? null : yearVal,
                monthVal === 'all' ? null : monthVal
            );
            setCourseWatchTimeData(data);
        } catch (error) {
            console.error('Error fetching course watch time data:', error);
        } finally {
            setWatchTimeLoading(false);
        }
    }, [user?.UserName, user?.email]);

    useEffect(() => {
        if (isWatchTimeInitialMount.current) {
            isWatchTimeInitialMount.current = false;
            return;
        }
        loadWatchTimeData(watchTimeMonth, watchTimeYear);
    }, [watchTimeMonth, watchTimeYear, loadWatchTimeData]);

    const handleSelectMonthFromChart = (monthName: string) => {
        const mIndex = monthNames.findIndex(m => m.toLowerCase() === monthName.toLowerCase());
        if (mIndex !== -1) {
            setWatchTimeMonth(String(mIndex + 1));
            setWatchTimeYear(String(currentYear));
            const el = document.getElementById("course-watch-time-section");
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    useEffect(() => {
        const loadDashboardData = async () => {
            const instructorId = user?.UserName || user?.email; // Fallback for testing
            if (!instructorId) {
                console.log("No user UserName or email found:", user);
                return;
            }
            
            console.log("Loading dashboard data for instructor:", instructorId);
            
            try {
                setLoading(true);
                const [stats, revenue, watchTimeData] = await Promise.all([
                    dashboardService.getDashboardStats(instructorId,selectedCourse==='all-courses'?null:selectedCourse),
                    dashboardService.getRevenueStatistics(instructorId, new Date().getFullYear()),
                    courseWatchTimeService.getCourseWatchTimeData(
                        instructorId,
                        'all',
                        watchTimeYear === 'all' ? null : watchTimeYear,
                        watchTimeMonth === 'all' ? null : watchTimeMonth
                    )
                ]);

                const revenueWithMonthNames = revenue.map((item) => {
                  let mIndex = parseInt(item.month, 10) - 1;
                  if (item.month.includes('-')) {
                    mIndex = parseInt(item.month.split('-')[1], 10) - 1;
                  }
                  return {
                    ...item,
                    month: monthNames[mIndex] || item.month,
                  };
                });
                setDashboardStats(stats);
                setRevenueStats(revenueWithMonthNames);
                setCourseWatchTimeData(watchTimeData);
              console.log("Course watch time data loaded:", watchTimeData);
            } catch (error) {
                console.error('Error loading dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        const fetchInstructorCourses = async () => {
                try {
                    setLoading(true);
                    
                    const instructorId = user?.UserName || user?.email;
                    if (!instructorId) {
                        console.log("No user email found");
                        return;
                    }
        
                    console.log("Fetching courses for instructor:", instructorId);
                    const instructorCourses = await getInstructorCourses(instructorId);
                    
                    // Transform Firebase data to match the UI structure
                    const transformedCourses: CourseDisplayData[] = instructorCourses.map(course => ({
                        ...course,
                        // Use real data from Firestore or fallback to calculated values
                        earnings: course.members?.length ? course.members.length * 100 : 0, // Calculate based on members
                        enrollments: course.members?.length || 0, // Use actual member count
                        ratings: course.members?.length ? Math.floor(course.members.length * 0.8) : 0, // Calculate based on members
                        ratingScore: 4.5 // Default rating, can be enhanced with real review data
                    }));
                    
                    console.log("Transformed courses:", transformedCourses);
                    setCourses(transformedCourses);
        
                } catch (err) {
                    console.error("Error fetching courses:", err);
                    
                } finally {
                    setLoading(false);
                }
            };

            const loadMonthlyRevenueData = async () => {
            try {
                setLoading(true);
                const currentYear = new Date().getFullYear();
                console.log("Loading monthly revenue data for year:", currentYear);
                
                // Call the new API endpoint
                const monthlyRevenueData = await apiService.get<Array<{
                    month: string;
                    year: number;
                    totalRevenue: number;
                    totalTax: number;
                    totalPlatformFee: number;
                    totalInstructorShare: number;
                    totalWatchTime: number;
                    processedDate: string | null;
                }>>(
                    `${API_BASE_URL}instructor/dashboard/monthly-revenue`,
                    { year: currentYear }
                );
                
                console.log("Monthly Revenue Data from API:", monthlyRevenueData);
                
                // Transform API data to match the expected format
                const transformedData = monthlyRevenueData.map(item => ({
                    month: item.month,
                    year: item.year,
                    totalRevenue: item.totalRevenue || 0,
                    totalTax: item.totalTax || 0,
                    totalPlatformFee: item.totalPlatformFee || 0,
                    totalInstructorShare: item.totalInstructorShare || 0,
                    subscriptionRevenue: 0,
                    courseRevenue: 0,
                    withoutHoldingTax: item.totalInstructorShare || 0,
                    processedDate: item.processedDate ? new Date(item.processedDate) : null,
                    breakdown: [],
                    watchMinutes: item.totalWatchTime || 0,
                    totalWatchTime: item.totalWatchTime || 0
                }));
                
                console.log("Transformed monthly revenue data:", transformedData);
                setRevenueMonthly(transformedData);
            } catch (error) {
                console.error("Error loading monthly revenue data:", error);
                // Create empty 12 months on error
                const currentYear = new Date().getFullYear();
                const emptyMonths = [];
                for (let i = 1; i <= 12; i++) {
                    const nextMonth = i === 12 ? 1 : i + 1;
                    const nextYear = i === 12 ? currentYear + 1 : currentYear;
                    const expectedPayoutDate = new Date(nextYear, nextMonth - 1, 15);
                    
                    emptyMonths.push({
                        month: `${currentYear}-${i.toString().padStart(2, '0')}`,
                        year: currentYear,
                        totalRevenue: 0,
                        totalTax: 0,
                        totalPlatformFee: 0,
                        totalInstructorShare: 0,
                        subscriptionRevenue: 0,
                        courseRevenue: 0,
                        withoutHoldingTax: 0,
                        processedDate: expectedPayoutDate,
                        breakdown: [],
                        watchMinutes: 0,
                        totalWatchTime: 0
                    });
                }
                setRevenueMonthly(emptyMonths);
            } finally {
                setLoading(false);
            }
        };
      loadMonthlyRevenueData();
      fetchInstructorCourses();
      loadDashboardData();
        
    }, [user?.UserName]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <img src="/icons/loader.gif" alt="Loading..." className="w-16 h-16 mb-4" />
                <p className="text-gray-600">Loading dashboard data...</p>
            </div>
        );
    }

    return(
        <div>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            {/* <Select onValueChange={(value) => setSelectedCourse(value)} value={selectedCourse || "all-courses"}>
            <SelectTrigger className="form-title mr-2 border-none shadow-none outline-none truncate max-w-lg">{selectedCourse
      ? courses.find((c) => c.id === selectedCourse)?.title || "All Courses"
      : "All Courses"}</SelectTrigger>
            <SelectContent>
              <SelectItem value='all-courses' className="cursor-pointer">All Courses</SelectItem>
              {courses.map((course) => (
                <SelectItem key={course.id} value={course.id} className="cursor-pointer">
                  {course.title.length > 30 ? course.title.substring(0, 30) + "..." : course.title}
                </SelectItem>
              ))}
            </SelectContent>
            </Select> */}
            {/* <ChevronDown size={20} className="text-gray-500" /> */}
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatsCard 
            title="Total Watch Time" 
            value={dashboardStats?.totalWatchtime ? `${dashboardStats.totalWatchtime.toLocaleString()} Min` : '0 Min'}
            growth={dashboardStats?.totalCourses.toString() || '0'}
            period="Total Courses" 
          />
          <StatsCard 
            title="Total Revenue" 
            value={`₹${dashboardStats?.totalRevenue.toLocaleString() || '0'}`}
            growth={dashboardStats?.currentMonthRevenue.toLocaleString() || '0'}
            period="This Month" 
          />
          <StatsCard 
            title="Total Enrollment" 
            value={dashboardStats?.totalEnrollments.toLocaleString() || '0'}
            growth={dashboardStats?.currentMonthEnrollments.toLocaleString() || '0'}
            period="This Month" 
          />
          <StatsCard 
            title="Total Students" 
            value={dashboardStats?.totalStudents.toLocaleString() || '0'}
            growth={dashboardStats?.totalCourses.toString() || '0'}
            period="Total Courses" 
          />
        </div>
        <RevenueChart 
          data={revenueStats} 
          onClick={() => setShowMonthWiseReport((prev) => !prev)}
          onSelectMonth={handleSelectMonthFromChart}
        />
        {showmonthWiseReport&&
            <MonthWiseReports data={revenueMonthly}/>
        }
        
        {/* Course-wise Watch Time Section */}
        {(() => {
          const paidCoursesCount = courseWatchTimeData.filter(c => c.isPaid !== false && c.pricingType !== 'free').length;
          const freeCoursesCount = courseWatchTimeData.filter(c => c.isPaid === false || c.pricingType === 'free').length;

          const filteredCourses = courseWatchTimeData.filter(course => {
            if (watchTimePricingFilter === 'all') return true;
            if (watchTimePricingFilter === 'paid') return course.isPaid !== false && course.pricingType !== 'free';
            if (watchTimePricingFilter === 'free') return course.isPaid === false || course.pricingType === 'free';
            return true;
          });

          const totalFilteredWatchTime = filteredCourses.reduce((sum, course) => {
            if (watchTimePricingFilter === 'paid') return sum + (course.paidWatchTime ?? course.totalWatchTime);
            if (watchTimePricingFilter === 'free') return sum + (course.freeWatchTime ?? course.totalWatchTime);
            return sum + course.totalWatchTime;
          }, 0);

          const totalFilteredStudents = filteredCourses.reduce((sum, course) => sum + course.totalStudents, 0);
          const avgFilteredWatchTime = totalFilteredStudents > 0 ? totalFilteredWatchTime / totalFilteredStudents : 0;

          const selectedPeriodLabel = (() => {
            if (watchTimeMonth === 'all' && watchTimeYear === 'all') return 'All Time';
            if (watchTimeMonth === 'all') return `Full Year ${watchTimeYear}`;
            const monthNum = parseInt(watchTimeMonth, 10);
            const monthTitle = monthNames[monthNum - 1] || watchTimeMonth;
            if (watchTimeYear === 'all') return `${monthTitle} (All Years)`;
            return `${monthTitle} ${watchTimeYear}`;
          })();

          return (
            <div className="mt-8" id="course-watch-time-section">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h2 className="text-2xl font-bold text-gray-900">Course-wise Watch Time</h2>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                      <Calendar className="w-3.5 h-3.5 text-orange-600" />
                      {selectedPeriodLabel}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Track performance, watch minutes, and student engagement across your courses
                  </p>
                </div>

                {/* Filters Area: Month, Year, and Course Type */}
                <div className="flex flex-wrap items-center gap-2.5 self-start lg:self-auto">
                  {/* Month Selector */}
                  <div className="w-[145px]">
                    <Select
                      value={watchTimeMonth}
                      onValueChange={(val) => {
                        setWatchTimeMonth(val);
                        if (val !== 'all' && watchTimeYear === 'all') {
                          setWatchTimeYear(String(currentYear));
                        }
                      }}
                    >
                      <SelectTrigger className="h-9 text-xs bg-white border-gray-300 shadow-xs">
                        <SelectValue placeholder="Select Month" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">All Months</SelectItem>
                        {monthNames.map((name, idx) => (
                          <SelectItem key={idx} value={String(idx + 1)}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Year Selector */}
                  <div className="w-[115px]">
                    <Select
                      value={watchTimeYear}
                      onValueChange={(val) => setWatchTimeYear(val)}
                    >
                      <SelectTrigger className="h-9 text-xs bg-white border-gray-300 shadow-xs">
                        <SelectValue placeholder="Select Year" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">All Years</SelectItem>
                        {yearsList.map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* All Time Reset Button */}
                  {(watchTimeMonth !== 'all' || watchTimeYear !== 'all') && (
                    <button
                      onClick={() => {
                        setWatchTimeMonth('all');
                        setWatchTimeYear('all');
                      }}
                      className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1 border border-gray-200"
                      title="Reset to All Time"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      <span>All Time</span>
                    </button>
                  )}

                  {/* Paid / Free Filter Controls */}
                  <div className="flex items-center p-1 bg-gray-100 rounded-xl border border-gray-200">
                    <button
                      onClick={() => setWatchTimePricingFilter('all')}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                        watchTimePricingFilter === 'all'
                          ? 'bg-white text-gray-900 shadow-xs'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span>All</span>
                      <span className="px-1.5 py-0.2 text-[10px] rounded-full bg-gray-200/80 text-gray-700">
                        {courseWatchTimeData.length}
                      </span>
                    </button>

                    <button
                      onClick={() => setWatchTimePricingFilter('paid')}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                        watchTimePricingFilter === 'paid'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'text-gray-600 hover:text-emerald-700'
                      }`}
                    >
                      <span>Paid</span>
                      <span className={`px-1.5 py-0.2 text-[10px] rounded-full ${
                        watchTimePricingFilter === 'paid' ? 'bg-emerald-700/80 text-white' : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {paidCoursesCount}
                      </span>
                    </button>

                    <button
                      onClick={() => setWatchTimePricingFilter('free')}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                        watchTimePricingFilter === 'free'
                          ? 'bg-blue-600 text-white shadow-xs'
                          : 'text-gray-600 hover:text-blue-700'
                      }`}
                    >
                      <span>Free</span>
                      <span className={`px-1.5 py-0.2 text-[10px] rounded-full ${
                        watchTimePricingFilter === 'free' ? 'bg-blue-700/80 text-white' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {freeCoursesCount}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-6 relative">
                {/* Watch Time Loading Overlay */}
                {watchTimeLoading && (
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center rounded-xl z-10 transition-opacity">
                    <div className="flex items-center gap-2.5 px-4 py-2 bg-white rounded-lg shadow-md border border-gray-200">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                      <span className="text-xs font-medium text-gray-700">Loading {selectedPeriodLabel}...</span>
                    </div>
                  </div>
                )}

                {/* Dynamically Filtered Metric Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50/80 border border-blue-100 p-4 rounded-xl">
                    <div className="flex items-center">
                      <div className="p-2.5 bg-blue-100 rounded-lg shrink-0">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-4 min-w-0">
                        <p className="text-xs font-medium text-gray-600">Total Watch Time</p>
                        <p className="text-2xl font-bold text-gray-900 truncate">
                          {formatWatchTime(totalFilteredWatchTime)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50/80 border border-green-100 p-4 rounded-xl">
                    <div className="flex items-center">
                      <div className="p-2.5 bg-green-100 rounded-lg shrink-0">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div className="ml-4 min-w-0">
                        <p className="text-xs font-medium text-gray-600">Total Students</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {totalFilteredStudents.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50/80 border border-purple-100 p-4 rounded-xl">
                    <div className="flex items-center">
                      <div className="p-2.5 bg-purple-100 rounded-lg shrink-0">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div className="ml-4 min-w-0">
                        <p className="text-xs font-medium text-gray-600">Avg. Watch Time</p>
                        <p className="text-2xl font-bold text-gray-900 truncate">
                          {formatWatchTime(avgFilteredWatchTime)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-orange-50/80 border border-orange-100 p-4 rounded-xl">
                    <div className="flex items-center">
                      <div className="p-2.5 bg-orange-100 rounded-lg shrink-0">
                        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <div className="ml-4 min-w-0">
                        <p className="text-xs font-medium text-gray-600">Total Courses</p>
                        <p className="text-2xl font-bold text-gray-900">{filteredCourses.length}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50/80">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Course Name</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Watch Time</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Students</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg. Watch Time</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Completion Rate</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Accessed</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredCourses.length > 0 ? (
                        filteredCourses.map((course) => {
                          const isPaidCourse = course.isPaid !== false && course.pricingType !== 'free';
                          const displayWatchTime = watchTimePricingFilter === 'paid' 
                            ? (course.paidWatchTime ?? course.totalWatchTime)
                            : watchTimePricingFilter === 'free'
                            ? (course.freeWatchTime ?? course.totalWatchTime)
                            : course.totalWatchTime;

                          return (
                            <tr key={course.courseId} className="hover:bg-gray-50/70 transition-colors">
                              <td className="px-5 py-3.5 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900 max-w-xs md:max-w-md truncate" title={course.courseTitle}>
                                  {course.courseTitle}
                                </div>
                              </td>
                              <td className="px-5 py-3.5 whitespace-nowrap">
                                {isPaidCourse ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    Paid
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                                    Free
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3.5 whitespace-nowrap">
                                <div className="text-sm text-gray-900 font-semibold">{formatWatchTime(displayWatchTime)}</div>
                              </td>
                              <td className="px-5 py-3.5 whitespace-nowrap">
                                <div className="text-sm text-gray-700">{course.totalStudents}</div>
                              </td>
                              <td className="px-5 py-3.5 whitespace-nowrap">
                                <div className="text-sm text-gray-700">{formatWatchTime(course.averageWatchTime)}</div>
                              </td>
                              <td className="px-5 py-3.5 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-16 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-primary h-2 rounded-full transition-all" 
                                      style={{ width: `${course.completionRate}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs text-gray-600 font-medium">{Math.round(course.completionRate)}%</span>
                                </div>
                              </td>
                              <td className="px-5 py-3.5 whitespace-nowrap">
                                <div className="text-xs text-gray-500">
                                  {course.lastAccessed ? (
                                    course.lastAccessed.toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })
                                  ) : (
                                    <span className="text-gray-400 italic">No activity</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={7} className="px-6 py-8 text-center text-gray-500 text-sm">
                            No {watchTimePricingFilter === 'paid' ? 'paid' : watchTimePricingFilter === 'free' ? 'free' : ''} courses found with watch time for {selectedPeriodLabel}.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

      </div>
    )
}

interface StatsCardProps {
  title: string;
  value: string;
  growth: string;
  period: string;
}

const StatsCard = ({ title, value, growth, period }: StatsCardProps) => {
    return (
      <div className="p-4 bg-white rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)] shadow-md flex-1">
        <h3 className="text-black text-xl font-normal font-['Inter'] leading-[30px] mb-2">{title}</h3>
        <div className="flex justify-between items-start">
          <div>
            
            <p className=" text-primary text-[27px] font-semibold font-['Inter'] leading-10">{value}</p>
          </div>
          {/* <div className="text-right">
            <p className="text-primary text-[15px] font-semibold font-['Inter'] leading-snug">{growth}</p>
            <p className="text-black text-[10px] font-semibold font-['Inter'] leading-[15px]">{period}</p>
          </div> */}
        </div>
      </div>
    );
  };


  interface RevenueChartProps {
      onClick: () => void;
      data: RevenueData[];
      onSelectMonth?: (monthName: string) => void;
    }

  const RevenueChart = ({ onClick, data, onSelectMonth }: RevenueChartProps) => {
    const chartData = data.map(item => ({
      month: item.month,
      value: item.revenue
    }));
  
    return (
      <div className="p-4 bg-white rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)] mt-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-gray-700 font-medium">Revenue Statistics</h2>
          <div className="flex items-center border border-gray-300 rounded-md px-2 py-1">
          <Calendar size={16} className="text-gray-500  mr-2" />
            <span className="text-sm text-gray-600">Monthly</span>
            
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData} 
              barSize={25} 
              margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
              onClick={(state: any) => {
                if (state && state.activePayload && state.activePayload.length > 0) {
                  const m = state.activePayload[0].payload.month;
                  if (m && onSelectMonth) {
                    onSelectMonth(m);
                  }
                }
              }}
            >
              <XAxis dataKey="month" axisLine={false} tickLine={false} />
              <YAxis
              dataKey="revenue"
                axisLine={false}
                tickLine={false}
                ticks={[0, 2500, 5000, 7500, 10000]}
                tickFormatter={(value) => `₹${value / 1000}k`}
              />
              <CartesianGrid vertical={false} color="#bbb" strokeDasharray="0" />
              <Bar 
                dataKey="value" 
                fill="#FF6B00" 
                width={0.20} 
                radius={[5, 5, 0, 0]}
                className="cursor-pointer hover:opacity-85 transition-opacity" 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-left mt-4">
          <Button className=" text-sm font-medium" onClick={onClick}>
            Check Revenue Report
          </Button>
        </div>
      </div>
    );
  };


  const MonthWiseReports = ({data}:any) =>{

    console.log("Monthly Revenue Data in Report Component:", data);
    console.log("Number of months received:", data?.length);

    if (!data || data.length === 0) {
      return (
        <div className="p-4 mt-4">
          <h1 className="py-2 text-[#414d55] text-base font-medium font-['Poppins'] leading-tight tracking-tight">Month Wise Report</h1>
          <div className="text-center py-8 text-gray-500">
            No revenue data available for the selected period.
          </div>
        </div>
      );
    }

      const tableData = data.map((row:any) => {
      let monthName = "";
      let year = row.year || new Date().getFullYear();
      if (row?.month?.includes("-")) {
        const parts = row.month.split("-");
        year = parts[0];
        const m = parseInt(parts[1], 10);
        monthName = monthNames[m - 1] || parts[1];
      } else if (!isNaN(parseInt(row?.month, 10))) {
        const m = parseInt(row.month, 10);
        monthName = monthNames[m - 1] || row.month;
      } else {
        monthName = row.month;
      }

      return {
        month: `${monthName} ${year}`,
        preTax: row.totalRevenue || 0,         // Pre-Tax Amount
        withoutHolding: row.withoutHoldingTax || 0,        // Without Holding Tax
        netEarning: row.totalInstructorShare || 0,   // Net Earning
        watchTime: row.totalWatchTime || 0,    // Total Watch Time
        payoutDate: row.processedDate
          ? format(
              row.processedDate.toDate ? row.processedDate.toDate() : new Date(row.processedDate),
              "dd MMM yyyy"
            )
          : "N/A",
      };
    });

    console.log("Table data after processing:", tableData);
    console.log("Months in table data:", tableData.map((t: any) => t.month));

    // Calculate totals
    const totals = tableData.reduce((acc: any, row: any) => ({
      preTax: acc.preTax + row.preTax,
      withoutHolding: acc.withoutHolding + row.withoutHolding,
      netEarning: acc.netEarning + row.netEarning,
      watchTime: acc.watchTime + row.watchTime,
    }), { preTax: 0, withoutHolding: 0, netEarning: 0, watchTime: 0 });
   
    return(
        <div className="p-4 mt-4">
            <h1 className="py-2 text-[#414d55] text-base font-medium font-['Poppins'] leading-tight tracking-tight">Month Wise Report</h1>
            <Table className="border">
          <TableHeader className="ins-table-header">
          <TableRow className="table-head-text">
            <TableHead>Time Period</TableHead>
            {/* <TableHead>Pre Tax Amount</TableHead>
            <TableHead>Without Holding Tax</TableHead> */}
            <TableHead>Net Earning</TableHead>
            <TableHead>Watch Time (Min)</TableHead>
            <TableHead>Expected Payout Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableData.map((row:any, index:number) => (
            <TableRow
              key={index}
              className="ins-table-row"
              // onClick={() => {
              //   window.location.hash = "#/instructor/monthly-revenue";
              // }}
            >
              <TableCell className="table-body-text">{row.month}</TableCell>
              {/* <TableCell className="table-body-text">₹{row.preTax.toLocaleString()}</TableCell>
              <TableCell className="table-body-text">₹{row.withoutHolding.toLocaleString()}</TableCell> */}
              <TableCell className="table-body-text">₹{row.netEarning.toLocaleString()}</TableCell>
              <TableCell className="table-body-text">{row.watchTime.toLocaleString()}</TableCell>
              <TableCell className="table-body-text">{row.payoutDate}</TableCell>
            </TableRow>
          ))}
          {/* Totals Row */}
          <TableRow className="ins-table-row font-semibold bg-gray-50">
            <TableCell className="table-body-text">Total</TableCell>
            {/* <TableCell className="table-body-text">₹{totals.preTax.toLocaleString()}</TableCell>
            <TableCell className="table-body-text">₹{totals.withoutHolding.toLocaleString()}</TableCell> */}
            <TableCell className="table-body-text">₹{totals.netEarning.toLocaleString()}</TableCell>
            <TableCell className="table-body-text">{totals.watchTime.toLocaleString()}</TableCell>
            <TableCell className="table-body-text">-</TableCell>
          </TableRow>
        </TableBody>
        </Table>
        </div>
    )
  }